import { getCopy } from '../utils/copy';

/**
 * 练习关: 点选匹配(找出和目标一样的图形,或选出正确的文字答案)
 *
 * 隐形学习原则: 答错不出现"❌错误"字样,只给温和的重试提示,可无限重试。
 *
 * 6-8 岁维度用纯 emoji 渲染(choice.emoji);9-12 岁维度题目大多是短语而不是
 * 单个图形,choice.label 是文字渲染的备选——两种用法通过 choices 里到底带
 * 的是 emoji 还是 label 字段区分,互不影响,不是新组件。
 */

/** 把一串 emoji 拆成一个个字形(🦐🦐🦐 → 3 个),兼容带变体选择符的 emoji */
export function splitEmoji(text = '') {
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    return [...new Intl.Segmenter('zh', { granularity: 'grapheme' }).segment(text)]
      .map((s) => s.segment)
      .filter((s) => s.trim());
  }
  return Array.from(text).filter((s) => s.trim());
}

/**
 * 一组 emoji:自动换行,每行最多 5 个 —— 像数数用的十格板,孩子一眼能数清。
 * 找规律题(🐟→🐟🐟→🐟🐟🐟→❓)按箭头分组,同一组不拆开,放不下时整组换行。
 */
function EmojiCluster({ text, size }) {
  const items = splitEmoji(text);
  const cell = size === 'lg' ? 'h-10 w-10 text-3xl' : 'h-8 w-8 text-2xl';
  const renderCell = (item, key) => (
    <span key={key} className={`flex items-center justify-center leading-none ${cell}`}>
      {item}
    </span>
  );

  if (items.includes('→')) {
    const groups = [[]];
    items.forEach((item) => (item === '→' ? groups.push([]) : groups[groups.length - 1].push(item)));
    return (
      <span className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2">
        {groups.map((group, g) => (
          <span key={g} className="flex items-center">
            {g > 0 && renderCell('→', 'arrow')}
            <span className="flex">{group.map((item, i) => renderCell(item, i))}</span>
          </span>
        ))}
      </span>
    );
  }

  const cols = Math.min(items.length, 5);
  return (
    <span className="grid justify-center gap-0.5" style={{ gridTemplateColumns: `repeat(${cols}, auto)` }}>
      {items.map((item, i) => renderCell(item, i))}
    </span>
  );
}

// 记住每道题上点错过的选项(仅前端展示用):作答后题目组件会被换 key 重新挂载,
// 组件内 state 会丢,所以放在模块级;重新挂载且没有错误反馈时(新一轮/答对)清空。
const wrongPicks = new Map();

const OPTION_LETTERS = 'ABCDEFGH';

/** 题干开头的装饰 emoji(如「🏯 中华…」)拆成独立图标,正文不再带前缀 */
const LEADING_EMOJI = /^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)\s+(?=\S)/u;
export function splitLeadingEmoji(text = '') {
  const m = LEADING_EMOJI.exec(text);
  return m ? { icon: m[1], body: text.slice(m[0].length) } : { icon: null, body: text };
}

function PracticePuzzle({ puzzle, onAnswer, feedback, disabled, ageBand }) {
  const { target, choices, prompt } = puzzle.params;
  if (!feedback && wrongPicks.has(puzzle.id)) wrongPicks.delete(puzzle.id);
  const wrongIds = feedback && !feedback.correct ? wrongPicks.get(puzzle.id) || new Set() : new Set();
  const pick = (choiceId) => {
    if (!wrongPicks.has(puzzle.id)) wrongPicks.set(puzzle.id, new Set());
    wrongPicks.get(puzzle.id).add(choiceId);
    onAnswer(choiceId);
  };
  const dim = (choice) => (wrongIds.has(choice.id) ? 'opacity-40' : '');
  // 答错的温和提示:放在题干与选项之间,长题目在手机上也一眼看得到
  const retryHint =
    feedback && !feedback.correct ? (
      <p role="alert" className="w-full text-center text-base font-medium text-orange-700">
        {getCopy(ageBand).retryHint}
      </p>
    ) : null;
  const { icon: targetIcon, body: targetBody } = splitLeadingEmoji(target);
  const isTextMode = choices.some((choice) => choice.label !== undefined);
  // 数数/比多少/找规律这类题,目标和选项都是一串 emoji,固定大小的方块放不下,
  // 换成上下排列、自动换行的布局;单个图形的题保持原来左右对照的紧凑布局
  const isCountMode =
    !isTextMode &&
    [target, ...choices.map((c) => c.emoji)].some((t) => splitEmoji(t).length > 1);

  return (
    <div className="flex w-full flex-col items-center gap-6 rounded-2xl border-2 border-black bg-white p-5 shadow-brutal sm:p-8">
      <p className="text-lg font-bold text-gray-800">{puzzle.title}</p>
      {/* 题干:告诉孩子要做什么。以前只有标题,"谁的鱼虾多"和"选一样多"长得一模一样 */}
      {prompt && (
        <p data-testid="puzzle-prompt" className="-mt-3 text-center text-lg font-medium text-gray-700">
          {prompt}
        </p>
      )}

      {isTextMode ? (
        <div className="flex w-full flex-col items-center gap-5">
          <div className="flex w-full items-start gap-3">
            {targetIcon && (
              <span aria-hidden="true" className="shrink-0 text-3xl leading-none">
                {targetIcon}
              </span>
            )}
            <p
              className={`min-w-0 flex-1 whitespace-pre-line break-words text-xl leading-relaxed ${
                !targetIcon && target.length <= 14 && !target.includes('\n') ? 'text-center' : 'text-left'
              }`}
            >
              {targetBody}
            </p>
          </div>
          {retryHint}
          <div className="flex w-full flex-col gap-3">
            {choices.map((choice, idx) => (
              <button
                key={choice.id}
                type="button"
                data-testid={`choice-${choice.id}`}
                disabled={disabled}
                onClick={() => pick(choice.id)}
                className={`flex min-h-[48px] items-center break-words rounded-xl border-2 border-black bg-amber-50 px-4 py-3 text-left
                           text-base font-semibold leading-snug text-gray-800 shadow-brutal-sm transition
                           hover:-translate-y-0.5 active:translate-y-0 active:shadow-none
                           disabled:opacity-50 ${dim(choice)}`}
              >
                <span
                  aria-hidden="true"
                  className="mr-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-black bg-white text-sm font-bold"
                >
                  {OPTION_LETTERS[idx] || idx + 1}
                </span>
                <span className="min-w-0 flex-1">{choice.label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : isCountMode ? (
        <div className="flex w-full flex-col items-center gap-4">
          <div
            data-testid="count-target"
            className="flex w-full justify-center rounded-xl border-2 border-dashed border-gray-400 bg-amber-50/40 px-2 py-3"
          >
            <EmojiCluster text={target} size="lg" />
          </div>
          <span className="text-2xl text-gray-400">⬇</span>
          {retryHint}
          <div className="flex w-full flex-col gap-3">
            {choices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                data-testid={`choice-${choice.id}`}
                disabled={disabled}
                onClick={() => pick(choice.id)}
                className={`flex w-full justify-center rounded-xl border-2 border-black bg-amber-50 px-2 py-2
                           shadow-brutal-sm transition hover:-translate-y-0.5 active:translate-y-0
                           active:shadow-none disabled:opacity-50 ${dim(choice)}`}
              >
                <EmojiCluster text={choice.emoji} />
              </button>
            ))}
          </div>
        </div>
      ) : (
        // 手机上一行要放下 目标 + 箭头 + 3 个选项:以前 20/16 号方块 + gap-6 比卡片宽,整页横向溢出
        <>
        {retryHint}
        <div className="flex items-center gap-2 sm:gap-6">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-gray-400 text-4xl sm:h-20 sm:w-20 sm:text-5xl">
            {target}
          </div>
          <span className="text-xl text-gray-400 sm:text-2xl">➜</span>
          <div className="flex gap-2 sm:gap-3">
            {choices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                data-testid={`choice-${choice.id}`}
                disabled={disabled}
                onClick={() => pick(choice.id)}
                className={`flex h-14 w-14 items-center justify-center rounded-xl border-2
                           border-black bg-amber-50 text-3xl shadow-brutal-sm transition sm:h-16 sm:w-16 sm:text-4xl
                           hover:-translate-y-0.5 active:translate-y-0 active:shadow-none
                           disabled:opacity-50 ${dim(choice)}`}
              >
                {choice.emoji}
              </button>
            ))}
          </div>
        </div>
        </>
      )}
    </div>
  );
}

export default PracticePuzzle;
